/**
 * PWA Install Banner Component
 * Shows a banner prompting users to install the PWA
 */

import React from "react";
import { X, Download, Smartphone } from "lucide-react";
import { useLocation } from "react-router-dom";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { useI18nStore } from "@/stores/i18nStore";

/**
 * Pages whose whole purpose is installing the app, where this banner would be a
 * second, competing "Install" next to the one the page already offers - and the
 * two do different things: the page downloads a real app, the banner adds a
 * home-screen entry. Two buttons, two outcomes, no way for a visitor to tell
 * them apart.
 */
const INSTALL_PAGES = ["/android-install"];

export const PWAInstallBanner: React.FC = () => {
  const { canInstall, promptInstall, dismissPrompt } = usePWAInstall();
  const { t } = useI18nStore();
  const { pathname } = useLocation();

  if (!canInstall || INSTALL_PAGES.includes(pathname)) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-md z-50 animate-slide-up">
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg shadow-2xl p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <Smartphone className="w-6 h-6 text-white" />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="text-white font-semibold text-sm mb-1">
              {t.installApp}
            </h3>
            <p className="text-white/90 text-xs leading-relaxed">
              {t.installAppDescription}
            </p>

            <div className="flex gap-2 mt-3">
              <button
                onClick={promptInstall}
                className="flex items-center gap-2 px-4 py-2 bg-white text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-50 transition-colors"
              >
                <Download className="w-4 h-4" />
                {t.install}
              </button>
              <button
                onClick={dismissPrompt}
                className="px-4 py-2 bg-white/20 text-white rounded-lg text-sm font-medium hover:bg-white/30 transition-colors"
              >
                {t.later}
              </button>
            </div>
          </div>

          <button
            onClick={dismissPrompt}
            className="flex-shrink-0 text-white/80 hover:text-white transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallBanner;
